import { useEffect, useCallback, useState } from 'react';
import { View, Text, Pressable, Switch, StyleSheet, Platform, Modal, ScrollView, Linking } from 'react-native';
import RNSliderImport from '@react-native-community/slider';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RNSlider = RNSliderImport as any;
import { useAppStore, ARABIC_FONTS, THEMES, VIEW_MODES, RECITERS } from '../store/appStore';
import { fetchAvailableTranslations } from '../services/api';
import type { AvailableTranslation } from '../types';
import { Fonts, MdiIcons } from '../styles/fonts';
import { useTheme } from '../styles/theme';

function Picker({ value, items, onValueChange }: {
  value: string | number;
  items: Array<{ label: string; value: string | number }>;
  onValueChange: (val: string) => void;
}) {
  const { colors } = useTheme();

  if (Platform.OS === 'web') {
    return (
      <View style={pickerStyles.container}>
        <select
          value={value}
          onChange={(e: any) => onValueChange(e.target.value)}
          style={{ fontSize: 14, padding: '4px 8px', borderRadius: 4, border: `1px solid ${colors.accordionBorder}`, backgroundColor: colors.cardBg, color: colors.primaryText, minWidth: 120 }}
        >
          {items.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
      </View>
    );
  }

  const [open, setOpen] = useState(false);
  const selected = items.find((i) => String(i.value) === String(value));

  return (
    <>
      <Pressable style={[pickerStyles.nativeButton, { borderColor: colors.accordionBorder, backgroundColor: colors.cardBg }]} onPress={() => setOpen(true)}>
        <Text style={[pickerStyles.nativeButtonText, { color: colors.primaryText }]}>{selected?.label ?? String(value)}</Text>
        <Text style={[pickerStyles.nativeButtonChevron, { color: colors.secondaryText }]}>▾</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="slide">
        <Pressable style={[pickerStyles.modalOverlay, { backgroundColor: colors.overlayBg }]} onPress={() => setOpen(false)} />
        <View style={[pickerStyles.modalSheet, { backgroundColor: colors.cardBg }]}>
          <View style={[pickerStyles.modalHeader, { borderBottomColor: colors.accordionBorder }]}>
            <Text style={[pickerStyles.modalTitle, { color: colors.primaryText }]}>Select</Text>
            <Pressable onPress={() => setOpen(false)}>
              <Text style={[pickerStyles.modalDoneText, { color: colors.interactiveColor }]}>Done</Text>
            </Pressable>
          </View>
          <ScrollView>
            {items.map((item) => {
              const isSelected = String(item.value) === String(value);
              return (
                <Pressable
                  key={item.value}
                  style={[pickerStyles.optionRow, { borderBottomColor: colors.accordionBorder }, isSelected && { backgroundColor: colors.selectedOptionBg }]}
                  onPress={() => { onValueChange(String(item.value)); setOpen(false); }}
                >
                  <Text style={[pickerStyles.optionText, { color: isSelected ? colors.accentText : colors.primaryText }, isSelected && { fontWeight: '600' }]}>
                    {item.label}
                  </Text>
                  {isSelected && <Text style={[pickerStyles.optionCheck, { color: colors.accentText }]}>✓</Text>}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </Modal>
    </>
  );
}

const pickerStyles = StyleSheet.create({
  container: {
    // @ts-expect-error web cursor
    cursor: 'pointer',
  },
  nativeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 120,
  },
  nativeButtonText: {
    fontSize: 13,
    flex: 1,
  },
  nativeButtonChevron: {
    fontSize: 12,
    marginLeft: 4,
  },
  modalOverlay: {
    flex: 1,
  },
  modalSheet: {
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  modalDoneText: {
    fontSize: 16,
    fontWeight: '600',
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  optionText: {
    flex: 1,
    fontSize: 15,
  },
  optionCheck: {
    fontSize: 16,
  },
});

function Slider({ value, min, max, onValueChange, color, trackColor }: {
  value: number;
  min: number;
  max: number;
  onValueChange: (val: number) => void;
  color: string;
  trackColor: string;
}) {
  if (Platform.OS === 'web') {
    const fillPct = ((value - min) / (max - min)) * 100;
    return (
      <View style={sliderStyles.container}>
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e: any) => onValueChange(Number(e.target.value))}
          style={{ width: '100%', accentColor: color, background: `linear-gradient(to right, ${color} ${fillPct}%, ${trackColor} ${fillPct}%)` }}
        />
      </View>
    );
  }
  return (
    <RNSlider
      style={sliderStyles.container}
      minimumValue={min}
      maximumValue={max}
      value={value}
      onValueChange={onValueChange}
      minimumTrackTintColor={color}
      maximumTrackTintColor={trackColor}
      thumbTintColor={color}
      step={1}
    />
  );
}

const sliderStyles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
});

export default function SettingsPanel() {
  const settings = useAppStore((s) => s.settings);
  const setSettings = useAppStore((s) => s.setSettings);
  const availableTranslations = useAppStore((s) => s.availableTranslations);
  const availableScripts = useAppStore((s) => s.availableScripts);
  const setAvailableTranslations = useAppStore((s) => s.setAvailableTranslations);
  const setAvailableScripts = useAppStore((s) => s.setAvailableScripts);
  const closeDrawers = useAppStore((s) => s.closeDrawers);
  const [openSection, setOpenSection] = useState(0);
  const { colors } = useTheme();

  useEffect(() => {
    if (Object.keys(availableTranslations).length === 0) {
      fetchAvailableTranslations()
        .then((result) => {
          const scripts: Array<{ code: string; title: string }> = [];
          const trans: Record<string, AvailableTranslation> = {};
          if (result['Arabic']) {
            for (const item of result['Arabic']) {
              scripts.push(item);
            }
            trans['arabic'] = {
              language: 'Arabic', title: 'Script',
              code: settings.current_script, checked: settings.show_arabic,
            };
          }
          const languages = Object.keys(result);
          for (const lang of languages) {
            if (lang === 'Arabic') continue;
            for (const item of result[lang]) {
              trans[item.code] = {
                ...item, language: lang,
                checked: settings.lang_array.includes(item.code),
              };
            }
          }
          setAvailableScripts(scripts);
          setAvailableTranslations(trans);
        })
        .catch(() => {});
    }
  }, []);

  const updateTheme = useCallback(
    (val: string) => {
      const idx = Number(val);
      setSettings({ theme: { ...THEMES[idx] } });
    },
    [setSettings],
  );

  const updateArabicFont = useCallback(
    (val: string) => {
      const idx = Number(val);
      setSettings({ arabic_font: { ...ARABIC_FONTS[idx] } });
    },
    [setSettings],
  );

  const updateViewMode = useCallback(
    (val: string) => {
      const idx = Number(val);
      setSettings({ view_mode: { ...VIEW_MODES[idx] } });
    },
    [setSettings],
  );

  const updateReciter = useCallback(
    (val: string) => {
      const idx = Number(val);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setSettings({ current_reciter: { ...RECITERS[idx] } as any });
    },
    [setSettings],
  );

  const toggleTranslation = useCallback(
    (code: string) => {
      const updated = { ...availableTranslations };
      if (updated[code]) {
        updated[code] = { ...updated[code], checked: !updated[code].checked };
      }
      setAvailableTranslations(updated);
    },
    [availableTranslations, setAvailableTranslations],
  );

  const handleDone = useCallback(() => {
    const langArray: string[] = [];
    let showArabic = settings.show_arabic;
    for (const [, val] of Object.entries(availableTranslations)) {
      if (val.title === 'Script') {
        showArabic = val.checked;
      } else if (val.checked) {
        langArray.push(val.code);
      }
    }
    setSettings({ lang_array: langArray, show_arabic: showArabic });
    closeDrawers();
  }, [availableTranslations, settings.show_arabic, setSettings, closeDrawers]);

  const toggleSection = (idx: number) => {
    setOpenSection(idx);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.rightPaneBg }]}>
      {/* Look & Feel */}
      <Pressable style={[styles.sectionHeader, { borderBottomColor: colors.accordionBorder }]} onPress={() => toggleSection(0)}>
        <Text style={[styles.sectionHeaderIcon, { color: colors.accordionHeader }]}>{MdiIcons.phonelink}</Text>
        <Text style={[styles.sectionHeaderText, { color: colors.accordionHeader }]}>Look & Feel</Text>
      </Pressable>
      {openSection === 0 && (
        <View style={styles.sectionBody}>
          <View style={styles.row}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>Arabic Font</Text>
            <Picker
              value={settings.arabic_font.id}
              items={ARABIC_FONTS.map((f) => ({ label: f.name, value: f.id }))}
              onValueChange={updateArabicFont}
            />
          </View>
          <View style={[styles.separator, { backgroundColor: colors.accordionBorder }]} />

          <View style={styles.row}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>Arabic Font Size</Text>
            <Text style={{
              fontFamily: settings.arabic_font.value,
              fontSize: Number(settings.arabic_font_size),
              paddingHorizontal: 5,
            }}>ق</Text>
          </View>
          <Slider
            value={Number(settings.arabic_font_size)}
            min={24} max={60}
            onValueChange={(v) => setSettings({ arabic_font_size: String(v) })}
            color={colors.interactiveColor}
            trackColor={colors.sliderTrackBg}
          />
          <View style={[styles.separator, { backgroundColor: colors.accordionBorder }]} />

          <View style={styles.row}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>Other Font Size</Text>
            <Text style={{
              fontSize: Number(settings.trans_font_size),
              paddingHorizontal: 5,
            }}>Q</Text>
          </View>
          <Slider
            value={Number(settings.trans_font_size)}
            min={14} max={40}
            onValueChange={(v) => setSettings({ trans_font_size: String(v) })}
            color={colors.interactiveColor}
            trackColor={colors.sliderTrackBg}
          />
          <View style={[styles.separator, { backgroundColor: colors.accordionBorder }]} />

          <View style={styles.row}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>Themes</Text>
            <Picker
              value={settings.theme.id}
              items={THEMES.map((t) => ({ label: t.name, value: t.id }))}
              onValueChange={updateTheme}
            />
          </View>

          <View style={styles.switchRow}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>Auto Switch Theme</Text>
            <Switch
              value={settings.auto_switch_theme}
              onValueChange={() => setSettings({ auto_switch_theme: !settings.auto_switch_theme })}
              trackColor={{ false: colors.sliderTrackBg, true: colors.interactiveColor }}
              thumbColor={settings.auto_switch_theme ? colors.switchThumb : colors.switchThumbInactive}
            />
          </View>

          <View style={styles.switchRow}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>Keep screen on</Text>
            <Switch
              value={settings.keep_screen_on}
              onValueChange={() => setSettings({ keep_screen_on: !settings.keep_screen_on })}
              trackColor={{ false: colors.sliderTrackBg, true: colors.interactiveColor }}
              thumbColor={settings.keep_screen_on ? colors.switchThumb : colors.switchThumbInactive}
            />
          </View>

          <View style={styles.switchRow}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>Surah Kahf Reminder</Text>
            <Switch
              value={settings.kahf_reminder}
              onValueChange={() => setSettings({ kahf_reminder: !settings.kahf_reminder })}
              trackColor={{ false: colors.sliderTrackBg, true: colors.interactiveColor }}
              thumbColor={settings.kahf_reminder ? colors.switchThumb : colors.switchThumbInactive}
            />
          </View>
          <View style={[styles.separator, { backgroundColor: colors.accordionBorder }]} />

          <View style={styles.row}>
            <Text style={[styles.label, { color: colors.settingsLabelText }]}>View Mode</Text>
            <Picker
              value={settings.view_mode.id}
              items={VIEW_MODES.map((v) => ({ label: v.name, value: v.id }))}
              onValueChange={updateViewMode}
            />
          </View>
          <View style={[styles.separator, { backgroundColor: colors.accordionBorder }]} />

          <View style={{ paddingVertical: 8 }}>
            <Text style={[styles.label, { color: colors.settingsLabelText, marginBottom: 8 }]}>Reciter</Text>
            <Picker
              value={settings.current_reciter?.id ?? 10}
              items={RECITERS.map((r) => ({ label: r.name, value: r.id }))}
              onValueChange={updateReciter}
            />
          </View>
        </View>
      )}

      <View style={[styles.settingsSeparator, { backgroundColor: colors.settingsSeparator }]} />

      {/* Translations */}
      <Pressable style={[styles.sectionHeader, { borderBottomColor: colors.accordionBorder }]} onPress={() => toggleSection(1)}>
        <Text style={[styles.sectionHeaderIcon, { color: colors.accordionHeader }]}>{MdiIcons.translate}</Text>
        <Text style={[styles.sectionHeaderText, { color: colors.accordionHeader }]}>Translations</Text>
      </Pressable>
      {openSection === 1 && (
        <View style={styles.sectionBody}>
          <Text style={[styles.label, { color: colors.settingsLabelText, marginBottom: 8 }]}>Choose Arabic Script</Text>
          <Picker
            value={settings.current_script}
            items={availableScripts.map((s) => ({ label: s.title, value: s.code }))}
            onValueChange={(code) => setSettings({ current_script: code })}
          />
          <View style={[styles.separator, { marginVertical: 12 }]} />

          {Object.entries(availableTranslations).map(([code, trans]) => (
            <View key={code} style={styles.checkboxRow}>
              <Pressable
                style={styles.checkbox}
                onPress={() => toggleTranslation(code)}
              >
                <View style={[styles.checkboxBox, { borderColor: trans.checked ? colors.interactiveColor : colors.sliderTrackBg, backgroundColor: trans.checked ? colors.interactiveColor : 'transparent' }]}>
                  {trans.checked && <Text style={styles.checkMark}>✓</Text>}
                </View>
                <Text style={[styles.checkboxLabel, { color: colors.settingsLabelText }]}>
                  {trans.language} - {trans.title}
                </Text>
              </Pressable>
            </View>
          ))}

          <Pressable style={[styles.doneBtn, { backgroundColor: colors.topBarBg }]} onPress={handleDone}>
            <Text style={styles.doneBtnText}>Done</Text>
          </Pressable>
        </View>
      )}

      <View style={[styles.settingsSeparator, { backgroundColor: colors.settingsSeparator }]} />

      {/* Attributions */}
      <Pressable style={[styles.sectionHeader, { borderBottomColor: colors.accordionBorder }]} onPress={() => toggleSection(2)}>
        <Text style={[styles.sectionHeaderIcon, { color: colors.accordionHeader }]}>{MdiIcons.naturePeople}</Text>
        <Text style={[styles.sectionHeaderText, { color: colors.accordionHeader }]}>Attributions</Text>
      </Pressable>
      {openSection === 2 && (
        <View style={styles.sectionBody}>
          <Text style={[styles.label, { marginBottom: 8, color: colors.settingsLabelText }]}>Attributions & original sources from:</Text>
          <Text style={[styles.attrLink, { color: colors.accordionHeader }]}>• TriniMuslims.com</Text>
          <Text style={[styles.attrLink, { color: colors.accordionHeader }]}>• Tanzil.net</Text>
          <Text style={[styles.attrLink, { color: colors.accordionHeader }]}>• Quran.com</Text>
          <Text style={[styles.attrLink, { color: colors.accordionHeader }]}>• EveryAyah.com</Text>
        </View>
      )}

      <View style={[styles.settingsSeparator, { backgroundColor: colors.settingsSeparator }]} />

      {/* Feedback */}
      <Pressable style={[styles.sectionHeader, { borderBottomColor: colors.accordionBorder }]} onPress={() => toggleSection(3)}>
        <Text style={[styles.sectionHeaderIcon, { color: colors.accordionHeader }]}>{MdiIcons.textSms}</Text>
        <Text style={[styles.sectionHeaderText, { color: colors.accordionHeader }]}>Feedback</Text>
      </Pressable>
      {openSection === 3 && (
        <View style={[styles.sectionBody, { alignItems: 'center' }]}>
          <Text style={[styles.label, { marginBottom: 8, color: colors.settingsLabelText }]}>
            Help us spread Qur'an with your feedback
          </Text>
          <Pressable style={[styles.doneBtn, { backgroundColor: colors.recitationBg }]} onPress={() => Linking.openURL('mailto:feedback@guidedverses.xyz')}>
            <Text style={[styles.doneBtnText, { color: colors.recitationCtrlIcon }]}>Send Feedback</Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 12,
    paddingBottom: 40,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    borderBottomWidth: 2,
  },
  sectionHeaderIcon: {
    fontFamily: Fonts.MDI,
    fontSize: 20,
    marginRight: 10,
  },
  sectionHeaderText: {
    fontSize: 16,
    fontWeight: '600',
  },
  sectionBody: {
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  label: {
    fontSize: 14,
    fontWeight: '700',
  },
  separator: {
    height: 1,
    marginVertical: 4,
  },
  settingsSeparator: {
    height: 2,
  },
  checkboxRow: {
    paddingVertical: 8,
  },
  checkbox: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxBox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderRadius: 3,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkMark: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  checkboxLabel: {
    fontSize: 14,
  },
  doneBtn: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 2,
    alignItems: 'center',
    marginTop: 16,
    // @ts-expect-error web shadow
    boxShadow: '2px 2px 4px 0 rgba(0,0,0,.26)',
  },
  doneBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    // @ts-expect-error web text-transform
    textTransform: 'uppercase',
  },
  attrLink: {
    fontSize: 14,
    paddingVertical: 3,
  },
});
